import React, { useState, useEffect } from 'react';
import { ExpandableText } from '@app/react-common/components/expandable-text/ExpandableText'; // Import your ExpandableText component
import { GEODeliveryAreaMethod } from '@app/pages/checkout-app/types';

export const LocationSettingsExpandableSettings = ({ shipping }) => {
  const [formattedAddress, setFormattedAddress] = useState('');
  const [geoDeliveryArea, setGeoDeliveryArea] = useState(null);
  const [geoDeliveryMethod, setGeoDeliveryMethod] = useState(null);
  const [listedStates, setListedStates] = useState(null);
  const [listedCities, setListedCities] = useState(null);
  const [excludeStates, setExcludeStates] = useState(null);
  const [excludedCities, setExcludedCities] = useState(null);
  const [listedStatesControl, setListedStatesControl] = useState(null);
  const [isInternationalMethod, setIsInternationalMethod] = useState(false);
  const [internationalDestinationCountryCodes, setInternationalDestinationCountryCodes] = useState(null);

  const DeliveryMethodMap = {
    [GEODeliveryAreaMethod.LISTED_STATES]: 'listed states',
    [GEODeliveryAreaMethod.LISTED_CITIES]: 'listed cities',
    [GEODeliveryAreaMethod.STATES_PEN_TOOL]: 'pen tool',
    [GEODeliveryAreaMethod.STATES_FROM_ADDRESS_RADIUS]: 'radius',
    [GEODeliveryAreaMethod.STATES_FROM_LIST]: 'list',

    [GEODeliveryAreaMethod.PEN_TOOL]: 'pen tool',
    [GEODeliveryAreaMethod.CITIES_FROM_LIST]: 'list',
    [GEODeliveryAreaMethod.CITIES_FROM_ADDRESS_RADIUS]: 'radius',

    [GEODeliveryAreaMethod.STREETS_FROM_ADDRESS_RADIUS]: 'radius',
    [GEODeliveryAreaMethod.STREETS_FROM_LIST]: 'list',

    [GEODeliveryAreaMethod.CUSTOM_PEN_TOOL]: 'pen tool',
    [GEODeliveryAreaMethod.CUSTOM_FROM_ADDRESS_RADIUS]: 'radius',
  };

  useEffect(() => {
    if (shipping) {
      const {
        deliveryAreaSelectMethod,
        shippedFromLocationAddress,
        shippingMethod,
        polygonInputValues,
        internationalSettings,
      } = shipping;

      const { category } = shippingMethod;
      setIsInternationalMethod(category?.includes('international'));
      if (shippedFromLocationAddress)
        setFormattedAddress(`${shippedFromLocationAddress?.country},  ${shippedFromLocationAddress.storeName}`);

      if (
        isInternationalMethod &&
        internationalSettings?.countriesAndRegions &&
        Object.keys(internationalSettings.countriesAndRegions).length
      ) {
        setInternationalDestinationCountryCodes(Object.keys(internationalSettings.countriesAndRegions).join(', '));
        return;
      }

      const polygonValues = polygonInputValues?.[0];
      if (polygonValues?.excludedStates?.length) {
        setExcludeStates(polygonValues.excludedStates.join(', '));
      } else {
        setExcludeStates(null);
      }
      if (polygonValues?.excludedCities?.length) {
        setExcludedCities(polygonValues.excludedCities.join(', '));
      } else {
        setExcludedCities(null);
      }
      if (polygonValues?.listedStates?.length && !polygonValues?.listedStatesControl) {
        setListedStates(polygonValues.listedStates.join(', '));
      } else {
        setListedStates(null);
      }
      if (polygonValues?.listedStatesControl && Object.keys(polygonValues.listedStatesControl).length > 0) {
        setListedStatesControl(Object.values<string[]>(polygonValues.listedStatesControl).flat().join(', '));
      } else {
        setListedStatesControl(null);
      }
      if (polygonValues?.listedCities?.length) {
        setListedCities(polygonValues.listedCities.join(', '));
      } else {
        setListedCities(null);
      }

      if (deliveryAreaSelectMethod === GEODeliveryAreaMethod.ALL_CITIES) {
        setGeoDeliveryArea(`Geo area everywhere in ${shippedFromLocationAddress?.country}`);
        setGeoDeliveryMethod(null);
      } else if (deliveryAreaSelectMethod?.includes('states')) {
        setGeoDeliveryArea('Geo area by states');
        setGeoDeliveryMethod(`Geo method by ${DeliveryMethodMap[deliveryAreaSelectMethod]}`);
      } else if (
        deliveryAreaSelectMethod?.includes('cities') ||
        (deliveryAreaSelectMethod === GEODeliveryAreaMethod.PEN_TOOL && category != 'local')
      ) {
        setGeoDeliveryArea('Geo area by cities');
        setGeoDeliveryMethod(`Geo method by ${DeliveryMethodMap[deliveryAreaSelectMethod]}`);
      } else if (
        deliveryAreaSelectMethod?.includes('streets') ||
        (deliveryAreaSelectMethod === GEODeliveryAreaMethod.PEN_TOOL && category == 'local')
      ) {
        setGeoDeliveryArea('Geo area by streets');
        setGeoDeliveryMethod(`Geo method by ${DeliveryMethodMap[deliveryAreaSelectMethod]}`);
      } else if (deliveryAreaSelectMethod?.includes('custom')) {
        setGeoDeliveryArea('Geo area by custom');
        setGeoDeliveryMethod(`Geo method by custom ${DeliveryMethodMap[deliveryAreaSelectMethod]}`);
      } else if (deliveryAreaSelectMethod === GEODeliveryAreaMethod.ZONES) {
        setGeoDeliveryArea('Geo area by zones');
        setGeoDeliveryMethod(null);
      } else if (deliveryAreaSelectMethod === GEODeliveryAreaMethod.ZIP_CODES) {
        setGeoDeliveryArea('Geo area by zip codes');
        setGeoDeliveryMethod(null);
      } else if (deliveryAreaSelectMethod === GEODeliveryAreaMethod.COUNTY) {
        setGeoDeliveryArea('Geo area by county');
        setGeoDeliveryMethod(null);
      }
    }
  }, [shipping, isInternationalMethod]);

  const rows = [`Shipped from: ${formattedAddress}`];

  if (isInternationalMethod) {
    rows.push(`Destination country codes: ${internationalDestinationCountryCodes}`);
  } else {
    if (geoDeliveryArea) {
      rows.push(geoDeliveryArea);
    }
    if (geoDeliveryMethod) {
      rows.push(geoDeliveryMethod);
    }

    if (listedStatesControl) {
      rows.push(`Listed states: ${listedStatesControl}`);
    }
    if (!listedStatesControl && listedStates) {
      rows.push(`Listed states: ${listedStates}`);
    }
    if (listedCities) {
      rows.push(`Listed cities: ${listedCities}`);
    }
    if (excludeStates) {
      rows.push(`Excluded states: ${excludeStates}`);
    }
    if (excludedCities) {
      rows.push(`Excluded cities: ${excludedCities}`);
    }
  }

  return <ExpandableText title={`location settings`} rows={rows} />;
};
